import { useState, useMemo } from "react";

/**
 * Custom hook for table sorting and pagination
 * @param {Array} data - The data array to sort and paginate
 * @param {Object} options - Configuration options
 * @param {string} options.defaultSortKey - Default column to sort by
 * @param {string} options.defaultSortOrder - Default sort order ('asc' or 'desc')
 * @param {number} options.defaultPageSize - Default items per page
 */
export function useTableSort(data, options = {}) {
  const {
    defaultSortKey = null,
    defaultSortOrder = "asc",
    defaultPageSize = 10
  } = options;

  const [sortKey, setSortKey] = useState(defaultSortKey);
  const [sortOrder, setSortOrder] = useState(defaultSortOrder);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  // Handle sort column click
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
    setCurrentPage(1); // Reset to first page on sort change
  };

  // Get nested value from object using dot notation
  const getNestedValue = (obj, path) => {
    return path.split(".").reduce((acc, part) => acc && acc[part], obj);
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortKey || !data.length) return [...data];

    return [...data].sort((a, b) => {
      let aVal = getNestedValue(a, sortKey);
      let bVal = getNestedValue(b, sortKey);

      // Handle null/undefined
      if (aVal == null) aVal = "";
      if (bVal == null) bVal = "";

      // Handle dates
      if (sortKey.includes("date") || sortKey.includes("_at")) {
        aVal = new Date(aVal).getTime() || 0;
        bVal = new Date(bVal).getTime() || 0;
      }
      // Handle numbers
      else if (typeof aVal === "number" || typeof bVal === "number") {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }
      // Handle strings
      else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }

      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortKey, sortOrder]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize]);

  // Calculate pagination info
  const totalPages = Math.ceil(sortedData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, sortedData.length);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      }
    }
    return pages;
  };

  return {
    // Sorting
    sortKey,
    sortOrder,
    handleSort,
    
    // Pagination
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    startIndex,
    endIndex,
    getPageNumbers,
    
    // Data
    sortedData,
    paginatedData,
    totalItems: sortedData.length
  };
}

export default useTableSort;
